import React, { lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Core pages
import HomePage from './pages/HomePage';
const ConstructionPage = lazy(() => import('./pages/ConstructionPage'));
const LeisurePage = lazy(() => import('./pages/LeisurePage'));
const HealthcarePage = lazy(() => import('./pages/HealthcarePage'));
const CGIStagingPage = lazy(() => import('./pages/CGIStagingPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));

// SEO Landing Pages
const MatterportVirtualToursPage = lazy(() => import('./pages/MatterportVirtualToursPage'));
const PointCloudSurveysPage = lazy(() => import('./pages/PointCloudSurveysPage'));
const ScanToBimPage = lazy(() => import('./pages/ScanToBimPage'));
const CommercialPropertyToursPage = lazy(() => import('./pages/CommercialPropertyToursPage'));
const CommercialServicePage = lazy(() => import('./pages/CommercialServicePage'));
const ConstructionCapturePage = lazy(() => import('./pages/ConstructionCapturePage'));
const MatterportServiceProviderPage = lazy(() => import('./pages/MatterportServiceProviderPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage'));
const CaseStudyPage = lazy(() => import('./pages/CaseStudyPage'));
const AreaPage = lazy(() => import('./pages/AreaPage'));
const LondonScanningPage = lazy(() => import('./pages/LondonScanningPage'));

// Blog Pages
const BlogIndexPage = lazy(() => import('./pages/blog/BlogIndexPage'));
const WhatIsDigitalTwinPage = lazy(() => import('./pages/blog/WhatIsDigitalTwinPage'));
const MatterportScanCostPage = lazy(() => import('./pages/blog/MatterportScanCostPage'));
const ThreeDScanningNHSPage = lazy(() => import('./pages/blog/ThreeDScanningNHSPage'));
const DroneSurveysCostUKPage = lazy(() => import('./pages/blog/DroneSurveysCostUKPage'));
const BIMLevel2Page = lazy(() => import('./pages/blog/BIMLevel2Page'));
const MatterportForArchitectsPage = lazy(() => import('./pages/blog/MatterportForArchitectsPage'));
const VirtualTourCommercialPropertyPage = lazy(() => import('./pages/blog/VirtualTourCommercialPropertyPage'));
const MatterportVsLaserScanningPage = lazy(() => import('./pages/blog/MatterportVsLaserScanningPage'));
const PointCloudSurveyCostPage = lazy(() => import('./pages/blog/PointCloudSurveyCostPage'));

function PageFallback() {
  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 16,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#1a3c6e',
    }}>
      <div style={{
        width: 36,
        height: 36,
        border: '3px solid #e6ecf5',
        borderTopColor: '#1a3c6e',
        borderRadius: '50%',
        animation: 'dsg-spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes dsg-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Core pages */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<HomePage />} />
            <Route path="/construction" element={<ConstructionPage />} />
            <Route path="/leisure" element={<LeisurePage />} />
            <Route path="/healthcare" element={<HealthcarePage />} />
            <Route path="/hologram" element={<HomePage />} />
            <Route path="/cgi" element={<CGIStagingPage />} />
            <Route path="/contact" element={<HomePage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPage />} />

            {/* SEO Landing Pages */}
            <Route path="/matterport-virtual-tours-london" element={<MatterportVirtualToursPage />} />
            <Route path="/point-cloud-surveys-london" element={<PointCloudSurveysPage />} />
            <Route path="/scan-to-bim-london" element={<ScanToBimPage />} />
            <Route path="/commercial-property-3d-tours-london" element={<CommercialPropertyToursPage />} />
            <Route path="/vacant-unit-virtual-tours" element={<CommercialServicePage slug="vacant-unit-virtual-tours" />} />
            <Route path="/office-fit-out-3d-tours" element={<CommercialServicePage slug="office-fit-out-3d-tours" />} />
            <Route path="/hotel-virtual-tours" element={<CommercialServicePage slug="hotel-virtual-tours" />} />
            <Route path="/retail-store-3d-scanning" element={<CommercialServicePage slug="retail-store-3d-scanning" />} />
            <Route path="/construction-progress-capture" element={<ConstructionCapturePage />} />
            <Route path="/matterport-service-provider-uk" element={<MatterportServiceProviderPage />} />
            <Route path="/pricing" element={<PricingPage />} />

            {/* Case studies + area pages (data in src/data) */}
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/case-studies/:slug" element={<CaseStudyPage />} />
            <Route path="/3d-laser-scanning-london" element={<LondonScanningPage />} />
            <Route path="/3d-scanning-manchester" element={<AreaPage slug="3d-scanning-manchester" />} />
            <Route path="/3d-scanning-birmingham" element={<AreaPage slug="3d-scanning-birmingham" />} />
            <Route path="/3d-scanning-bristol" element={<AreaPage slug="3d-scanning-bristol" />} />
            <Route path="/3d-scanning-leeds" element={<AreaPage slug="3d-scanning-leeds" />} />
            <Route path="/3d-scanning-newcastle" element={<AreaPage slug="3d-scanning-newcastle" />} />
            <Route path="/3d-scanning-sheffield" element={<AreaPage slug="3d-scanning-sheffield" />} />
            <Route path="/3d-scanning-liverpool" element={<AreaPage slug="3d-scanning-liverpool" />} />
            <Route path="/3d-scanning-cardiff" element={<AreaPage slug="3d-scanning-cardiff" />} />

            {/* Blog */}
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/what-is-a-digital-twin" element={<WhatIsDigitalTwinPage />} />
            <Route path="/blog/how-much-does-matterport-scan-cost-uk" element={<MatterportScanCostPage />} />
            <Route path="/blog/3d-scanning-nhs-hospitals" element={<ThreeDScanningNHSPage />} />
            <Route path="/blog/drone-survey-costs-uk" element={<DroneSurveysCostUKPage />} />
            <Route path="/blog/bim-level-2-requirements-uk" element={<BIMLevel2Page />} />
            <Route path="/blog/matterport-for-architects" element={<MatterportForArchitectsPage />} />
            <Route path="/blog/virtual-tours-commercial-property" element={<VirtualTourCommercialPropertyPage />} />
            <Route path="/blog/matterport-vs-laser-scanning-which-do-you-need" element={<MatterportVsLaserScanningPage />} />
            <Route path="/blog/point-cloud-survey-cost-uk" element={<PointCloudSurveyCostPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
